import { useRef, useState } from 'react';
import { useTexts } from '../../../hooks/texts.hook';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';
import { IFormBaseSettings } from '../form-base-props.interface';
import ContainerItemBlockHeading from '../../Shared/ContainerItemBlockHeading';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import RadioFormGroup from '../../Shared/RadioFormGroup';
import Alert from '../../Shared/Alert';
import ValidationInfo from '../../../validation/validation-info';
import LogService from '../../../services/log-service/log.service';
import AntiforgeryService from '../../../services/anti-forgery.service';
import { LanguageService } from '../../../services/language.service';
import { IMultipleYesNoFeedbackFormApiModel } from './Models/multiple-yes-no-feedback-form.api-model';
import { MultipleYesNoFeedbackAnswerOption } from './Models/multiple-yes-no-feedback-answer-option.enum';

export interface IMultipleYesNoFeedbackFormTexts {
  heading: string;
  textBody: string;
  questionOne: string;
  questionTwo: string;
  questionThree: string;
  questionFour: string;
  questionFive: string;
  submit: string;
  submitted: string;
  formsRequiredFieldsMessage: string;
}

export interface IMultipleYesNoFeedbackFormSettings extends IFormBaseSettings {
  showQuestionsOnlyWhenPreviousAnswered: boolean;
}

type IMultipleYesNoFeedbackFormProps = IServerSideRendered<IMultipleYesNoFeedbackFormTexts, IMultipleYesNoFeedbackFormSettings>;

function MultipleYesNoFeedbackForm({ settings, texts }: IMultipleYesNoFeedbackFormProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const [selectedOptionQuestionOne, setSelectedOptionQuestionOne] = useState<MultipleYesNoFeedbackAnswerOption | undefined>(undefined);
  const [selectedOptionQuestionTwo, setSelectedOptionQuestionTwo] = useState<MultipleYesNoFeedbackAnswerOption | undefined>(undefined);
  const [selectedOptionQuestionThree, setSelectedOptionQuestionThree] = useState<MultipleYesNoFeedbackAnswerOption | undefined>(undefined);
  const [selectedOptionQuestionFour, setSelectedOptionQuestionFour] = useState<MultipleYesNoFeedbackAnswerOption | undefined>(undefined);
  const [selectedOptionQuestionFive, setSelectedOptionQuestionFive] = useState<MultipleYesNoFeedbackAnswerOption | undefined>(undefined);
  const [show, setShow] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // === REFS ===
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === VARIABLES ===
  const showQuestionTwo = !!texts.questionTwo && (!settings.showQuestionsOnlyWhenPreviousAnswered || !!selectedOptionQuestionOne);
  const showQuestionThree = !!texts.questionThree && (!settings.showQuestionsOnlyWhenPreviousAnswered || (!!selectedOptionQuestionTwo && showQuestionTwo));
  const showQuestionFour = !!texts.questionFour && (!settings.showQuestionsOnlyWhenPreviousAnswered || (!!selectedOptionQuestionThree && showQuestionThree));
  const showQuestionFive = !!texts.questionFive && (!settings.showQuestionsOnlyWhenPreviousAnswered || (!!selectedOptionQuestionFour && showQuestionFour));
  const isFormValid =
    !!selectedOptionQuestionOne &&
    (!showQuestionTwo || !!selectedOptionQuestionTwo) &&
    (!showQuestionThree || !!selectedOptionQuestionThree) &&
    (!showQuestionFour || !!selectedOptionQuestionFour) &&
    (!showQuestionFive || !!selectedOptionQuestionFive);
  const showSubmitButton = settings.alwaysShowSubmitButton || isFormValid;
  const headingElement = (
    <ContainerItemBlockHeading headingText={texts.heading} headingLevel={settings.headingLevel} headingLevelClass={settings.headingLevelClass} headingVisuallyHidden={settings.headingVisuallyHidden} />
  );
  const radioFormOptions = [
    { value: MultipleYesNoFeedbackAnswerOption.Yes, text: t.yes },
    { value: MultipleYesNoFeedbackAnswerOption.No, text: t.no },
  ];

  // === EVENT HANDLERS ===
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedOptionQuestionOne || isSubmitting || !honeyPotRef.current.isValid()) return;

    if (!isFormValid) {
      return;
    }
    setIsSubmitting(true);
    setShowErrorMessage(false);

    try {
      const requestData: IMultipleYesNoFeedbackFormApiModel = {
        $type: 'MultipleYesNoFeedback',
        blockContentGuid: settings.blockContentGuid,
        pageContentGuid: settings.pageContentGuid,
        answerOne: selectedOptionQuestionOne,
        answerTwo: selectedOptionQuestionTwo,
        answerThree: selectedOptionQuestionThree,
        answerFour: selectedOptionQuestionFour,
        answerFive: selectedOptionQuestionFive,
      };

      const formData = new FormData();
      formData.append('feedbackForm', JSON.stringify(requestData));

      const response = await fetch(`/api/forms/submit?language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitted(true);
    } catch (error) {
      LogService.logError(error);
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      className={`feedback-form multiple-yes-no-feedback-form ${settings.useBorder ? 'border p-4 rounded' : ''} position-relative ${settings.colorTheme} ${show ? '' : 'd-none'} ${settings.className ?? ''}`}
    >
      {settings.useCloseButton && <button type="button" className="btn-close position-absolute top-0 end-0" aria-label={t.close} onClick={() => setShow(false)} />}
      {isSubmitted && (
        <div role="status" className="mb-0">
          {texts.submitted}
        </div>
      )}
      {!isSubmitted && (
        <>
          {texts.heading?.trim() && settings.useCloseButton ? <div className="pe-4">{headingElement}</div> : headingElement}

          {texts.textBody && <p>{texts.textBody}</p>}
          <form onSubmit={handleSubmit}>
            <RadioFormGroup
              options={radioFormOptions}
              rowLayout={true}
              className={`${settings.useCloseButton && !texts.heading?.trim() ? 'pe-4 ' : ''}${showSubmitButton || showQuestionTwo || showQuestionThree || showQuestionFour || showQuestionFive ? 'mb-3' : ''}`.trim()}
              labelText={texts.questionOne}
              state={{ value: selectedOptionQuestionOne?.toString() ?? '', validationInfo: new ValidationInfo() }}
              onChange={(option) => setSelectedOptionQuestionOne(option ? (option as MultipleYesNoFeedbackAnswerOption) : undefined)}
            />
            {showQuestionTwo && (
              <RadioFormGroup
                options={radioFormOptions}
                rowLayout={true}
                className={showSubmitButton || showQuestionThree || showQuestionFour || showQuestionFive ? 'mb-3' : ''}
                labelText={texts.questionTwo}
                state={{ value: selectedOptionQuestionTwo?.toString() ?? '', validationInfo: new ValidationInfo() }}
                onChange={(option) => setSelectedOptionQuestionTwo(option ? (option as MultipleYesNoFeedbackAnswerOption) : undefined)}
              />
            )}
            {showQuestionThree && (
              <RadioFormGroup
                options={radioFormOptions}
                rowLayout={true}
                className={showSubmitButton || showQuestionFour || showQuestionFive ? 'mb-3' : ''}
                labelText={texts.questionThree}
                state={{ value: selectedOptionQuestionThree?.toString() ?? '', validationInfo: new ValidationInfo() }}
                onChange={(option) => setSelectedOptionQuestionThree(option ? (option as MultipleYesNoFeedbackAnswerOption) : undefined)}
              />
            )}
            {showQuestionFour && (
              <RadioFormGroup
                options={radioFormOptions}
                rowLayout={true}
                className={showSubmitButton || showQuestionFive ? 'mb-3' : ''}
                labelText={texts.questionFour}
                state={{ value: selectedOptionQuestionFour?.toString() ?? '', validationInfo: new ValidationInfo() }}
                onChange={(option) => setSelectedOptionQuestionFour(option ? (option as MultipleYesNoFeedbackAnswerOption) : undefined)}
              />
            )}
            {showQuestionFive && (
              <RadioFormGroup
                options={radioFormOptions}
                rowLayout={true}
                className={showSubmitButton ? 'mb-3' : ''}
                labelText={texts.questionFive}
                state={{ value: selectedOptionQuestionFive?.toString() ?? '', validationInfo: new ValidationInfo() }}
                onChange={(option) => setSelectedOptionQuestionFive(option ? (option as MultipleYesNoFeedbackAnswerOption) : undefined)}
              />
            )}
            <Alert show={showErrorMessage} heading={t.generalError} closable={false} className="mb-3" />
            <HoneyPot ref={honeyPotRef} />
            <button type="submit" className="feedback-form__submit-button btn btn-primary w-100" hidden={!showSubmitButton} disabled={!isFormValid || isSubmitting}>
              {texts.submit}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default MultipleYesNoFeedbackForm;
